import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Stack,
  Typography,
} from "@mui/material";

/**
 * Prompt to create/update admin_inventory price config (unit_cost + unit_price).
 * Quantity defaults to 0; location defaults to DK11.
 */
export default function PurchaseOrderAddPricingDialog({
  open,
  product,
  defaultLocationCode = "DK11",
  submitting = false,
  error = null,
  submitLabel = "Save",
  onClose,
  onSubmit,
}) {
  const [unitCost, setUnitCost] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setUnitCost(
      product?.unit_cost != null && product.unit_cost !== ""
        ? String(product.unit_cost)
        : "",
    );
    setUnitPrice(
      product?.unit_price != null && product.unit_price !== ""
        ? String(product.unit_price)
        : "",
    );
    setLocalError(null);
  }, [open, product]);

  const handleSubmit = () => {
    const cost = Number(unitCost);
    const price = Number(unitPrice);
    if (!Number.isFinite(cost) || !(cost > 0)) {
      setLocalError("Selling Price is required and must be greater than 0.");
      return;
    }
    if (!Number.isFinite(price) || !(price > 0)) {
      setLocalError("MRP is required and must be greater than 0.");
      return;
    }
    if (cost > price) {
      setLocalError("Selling Price must not exceed MRP.");
      return;
    }
    setLocalError(null);
    onSubmit({
      unit_cost: cost,
      unit_price: price,
      quantity_on_hand: 0,
      location_code: defaultLocationCode,
    });
  };

  const name = product?.product_name || "Product";
  const sku = product?.sku || product?.article_code || "";

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 600 }}>Add price config</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          No warehouse price config was found for this product. Enter Selling Price
          and MRP to create admin inventory at{" "}
          <strong>{defaultLocationCode}</strong> (quantity can be 0).
        </DialogContentText>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {name}
        </Typography>
        {sku ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Article / SKU: {sku}
          </Typography>
        ) : null}
        <Stack spacing={2}>
          <TextField
            label="Selling Price *"
            type="number"
            value={unitCost}
            onChange={(e) => setUnitCost(e.target.value)}
            inputProps={{ min: 0.01, step: "0.01" }}
            fullWidth
            required
            disabled={submitting}
          />
          <TextField
            label="MRP *"
            type="number"
            value={unitPrice}
            onChange={(e) => setUnitPrice(e.target.value)}
            inputProps={{ min: 0.01, step: "0.01" }}
            fullWidth
            required
            disabled={submitting}
          />
        </Stack>
        {(localError || error) && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {localError || error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          variant="contained"
          sx={{ bgcolor: "#e90000", "&:hover": { bgcolor: "#c00000" } }}
        >
          {submitting ? "Saving…" : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
